import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Helper to generate initials from a name
const getInitials = (name: string) => {
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
};

const UserDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token, logout } = useAuth();

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [userProfile, setUserProfile] = useState<any>(null);
  const [usageData, setUsageData] = useState<any>(null);
  const [loginStatus, setLoginStatus] = useState<any>(null);
  const [inspectionStats, setInspectionStats] = useState<{ total: number; by_type: { container: number; pilot: number; fabric: number; line: number; final: number }; last_active: string | null } | null>(null);
  const [imgError, setImgError] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Edit Profile States
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);
  const [editPhone, setEditPhone] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [successTitle, setSuccessTitle] = useState('Success!');
  const [successMessage, setSuccessMessage] = useState('The action was completed successfully.');

  // Change Password States
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Module Assignment States
  const [allModules, setAllModules] = useState<any[]>([]);
  const [selectedModuleIds, setSelectedModuleIds] = useState<string[]>([]);
  const [isModulesLoading, setIsModulesLoading] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

        // Fetch User Profile
        const profileRes = await fetch(`${baseUrl}/api/v1/users/${id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (profileRes.status === 401) {
          logout();
          return;
        }

        const profileData = await profileRes.json();
        if (!profileRes.ok || !profileData.success) {
          throw new Error(profileData.message || 'Failed to fetch user profile');
        }

        // Fetch Usage Stats
        const usageRes = await fetch(`${baseUrl}/api/v1/users/usage/${id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (usageRes.status === 401) {
          logout();
          return;
        }

        const usageJson = await usageRes.json();
        if (!usageRes.ok || !usageJson.success) {
          throw new Error(usageJson.message || 'Failed to fetch user usage stats');
        }

        // Fetch Login Status
        const statusRes = await fetch(`${baseUrl}/api/v1/users/status/${id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        const statusJson = await statusRes.json();

        // Fetch All Modules
        const allModulesRes = await fetch(`${baseUrl}/api/v1/users/modules/all`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });
        const allModulesData = await allModulesRes.json();

        // Fetch User Assigned Modules
        const userModulesRes = await fetch(`${baseUrl}/api/v1/users/modules/${id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });
        const userModulesData = await userModulesRes.json();

        // Fetch Inspection Stats
        try {
          const statsRes = await fetch(`${baseUrl}/api/v1/users/${id}/inspection-stats`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { Authorization: `Bearer ${token}` }),
            },
          });
          const statsJson = await statsRes.json();
          if (statsJson.success) setInspectionStats(statsJson.data);
        } catch (_) {}

        setUserProfile(profileData.data);
        setUsageData(usageJson);
        setLoginStatus(statusJson.success ? statusJson : { isLoggedIn: false });
        setAllModules(allModulesData.success ? allModulesData.data : []);
        setSelectedModuleIds(userModulesData.success ? userModulesData.data.map((m: any) => m.id) : []);

        // Initialize Edit States
        setEditName(profileData.data.name || '');
        setEditEmail(profileData.data.email || '');
        setEditRole(profileData.data.role || '');
        setEditIsActive(profileData.data.is_active);
        setEditPhone(profileData.data.phone || '');
        setPreviewUrl(profileData.data.profileImage || null);
      } catch (err: any) {
        setError(err.message || 'An error occurred while fetching user data.');
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchUserData();
    }
  }, [id, token]);

  const handleDeleteUser = async () => {
    if (!id) return;

    try {
      setIsDeleting(true);
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

      const response = await fetch(`${baseUrl}/api/v1/users/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (response.status === 401) {
        logout();
        return;
      }

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to delete user');
      }

      setIsDeleteModalOpen(false);
      navigate('/user-management');
    } catch (err: any) {
      alert(err.message || 'An error occurred while deleting the user.');
    } finally {
      setIsDeleting(false);
    }
  };

  const resetPasswordForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setAdminPassword('');
    setPasswordError(null);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);

    if (newPassword !== confirmNewPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }

    try {
      setIsUpdatingPassword(true);
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

      const response = await fetch(`${baseUrl}/api/v1/users/change-password/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          adminPassword,
          currentPassword,
          newPassword
        }),
      });

      // Only logout if it's a genuine 401 Unauthorized (session expired)
      if (response.status === 401) {
        logout();
        return;
      }

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to update password');
      }

      setSuccessTitle('Password Changed!');
      setSuccessMessage('The user\'s password has been updated successfully. They can now log in using their new credentials.');
      setIsPasswordModalOpen(false);
      resetPasswordForm();
      setIsSuccessModalOpen(true);
    } catch (err: any) {
      setPasswordError(err.message || 'An error occurred while updating the password.');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleSaveChanges = async () => {
    try {
      setIsSaving(true);
      setError(null);
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

      const formData = new FormData();
      formData.append('name', editName);
      formData.append('email', editEmail);
      formData.append('role', editRole);
      formData.append('is_active', String(editIsActive));
      formData.append('phone', editPhone);

      if (selectedFile) {
        formData.append('profileImage', selectedFile);
      }

      const response = await fetch(`${baseUrl}/api/v1/users/${id}`, {
        method: 'PUT',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: formData,
      });

      if (response.status === 401) {
        logout();
        return;
      }

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to save changes');
      }

      // Update local profile state
      setUserProfile(data.data);

      // Save Module Assignments
      const modulesResponse = await fetch(`${baseUrl}/api/v1/users/modules/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ moduleIds: selectedModuleIds }),
      });

      if (!modulesResponse.ok) {
        throw new Error('Profile updated, but failed to save module assignments');
      }

      setSuccessTitle('User Updated!');
      setSuccessMessage('The user profile details and module assignments have been updated successfully.');
      setIsSuccessModalOpen(true);
      setSelectedFile(null);
    } catch (err: any) {
      alert(err.message || 'An error occurred while saving changes.');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleModule = (moduleId: string) => {
    setSelectedModuleIds(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const fmtMinutes = (minutes: number) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}m`;
  };

  // Total in-app active time accumulated across all sessions (stored in seconds).
  const fmtSeconds = (seconds: number | null | undefined) => {
    if (!seconds || seconds <= 0) return '—';
    const totalMinutes = Math.floor(seconds / 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  // Format the helper dates safely
  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-[#136bce]"></div>
        <p className="mt-4 text-sm font-medium text-slate-500">Loading user details...</p>
      </div>
    );
  }

  if (error || !userProfile) {
    return (
      <div className="w-full">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 shadow-sm">
          <div className="flex items-center">
            <span className="material-symbols-outlined mr-3 text-red-500">error</span>
            <h3 className="text-sm font-medium text-red-800">{error || 'User not found'}</h3>
          </div>
          <button
            onClick={() => navigate('/user-management')}
            className="mt-4 inline-flex items-center text-sm font-medium text-red-700 hover:text-red-600 hover:underline"
          >
            ← Back to User Management
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full pb-12 animate-in fade-in duration-500">
      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={() => navigate('/user-management')}
          className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-500 shadow-sm border border-slate-200 hover:text-slate-700 hover:bg-slate-50 transition-colors"
        >
          <span className="material-symbols-outlined text-[20px]">arrow_back</span>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">User Details</h1>
          <p className="text-sm text-slate-500">View and edit user profile information</p>
        </div>
      </div>

      <div className="mx-auto max-w-4xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 flex items-center justify-between px-8 py-6 bg-slate-50/50">
          <div className="flex items-center gap-4">
            <div className="relative group">
              {previewUrl && !imgError ? (
                <img
                  src={previewUrl}
                  alt={editName}
                  className="h-16 w-16 rounded-full object-cover ring-4 ring-white shadow-sm"
                  onError={() => setImgError(true)}
                />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 text-xl font-bold text-blue-600 ring-4 ring-white shadow-sm">
                  {getInitials(editName)}
                </div>
              )}
              <label
                htmlFor="profile-upload"
                className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer text-white"
              >
                <span className="material-symbols-outlined text-[20px]">photo_camera</span>
                <input
                  id="profile-upload"
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setSelectedFile(file);
                      setPreviewUrl(URL.createObjectURL(file));
                    }
                  }}
                />
              </label>
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">{userProfile.name}</h2>
              <p className="text-sm text-slate-500 capitalize">{userProfile.role} • {userProfile.employee_id || 'N/A'}</p>
            </div>
            <div className="ml-auto flex items-center gap-3">
              {loginStatus && (
                <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset ${loginStatus.isLoggedIn ? 'bg-blue-50 text-blue-700 ring-blue-600/20' : 'bg-slate-50 text-slate-600 ring-slate-500/20'}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${loginStatus.isLoggedIn ? 'bg-blue-600 animate-pulse' : 'bg-slate-400'}`}></span>
                  {loginStatus.isLoggedIn ? 'Online' : 'Offline'}
                </span>
              )}
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset ${userProfile.is_active ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' : 'bg-rose-50 text-rose-700 ring-rose-600/20'}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${userProfile.is_active ? 'bg-emerald-600' : 'bg-rose-600'}`}></span>
                {userProfile.is_active ? 'Active' : 'Disabled'}
              </span>
            </div>
          </div>
        </div>

        {/* Analytics Section */}
        {usageData && (
          <div className="px-8 pt-8 pb-4">
            <h3 className="mb-4 text-sm font-bold text-slate-900 uppercase tracking-wider">Usage Analytics</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="rounded-lg border border-slate-100 bg-blue-50/30 p-4">
                <p className="text-xs font-medium text-slate-500 mb-1 flex items-center"><span className="material-symbols-outlined text-[16px] mr-1 text-blue-500">login</span> Total Logins</p>
                <p className="text-2xl font-bold text-slate-900">{usageData.total_logins}</p>
              </div>
              <div className="rounded-lg border border-slate-100 bg-emerald-50/30 p-4">
                <p className="text-xs font-medium text-slate-500 mb-1 flex items-center"><span className="material-symbols-outlined text-[16px] mr-1 text-emerald-500">schedule</span> Total Minutes used</p>
                <p className="text-2xl font-bold text-slate-900">{fmtMinutes(usageData.total_minutes)}</p>
              </div>
              <div className="rounded-lg border border-slate-100 bg-purple-50/30 p-4">
                <p className="text-xs font-medium text-slate-500 mb-1 flex items-center"><span className="material-symbols-outlined text-[16px] mr-1 text-purple-500">history</span> Last Session Length</p>
                <p className="text-2xl font-bold text-slate-900">{fmtMinutes(usageData.last_usage_minutes)}</p>
              </div>
              <div className="rounded-lg border border-slate-100 bg-slate-50 p-4">
                <p className="text-xs font-medium text-slate-500 mb-1 flex items-center"><span className="material-symbols-outlined text-[16px] mr-1 text-slate-400">calendar_month</span> Last Login Date</p>
                <p className="text-sm font-bold text-slate-900 mt-2">{formatDateTime(usageData.last_login)}</p>
              </div>
            </div>

            {/* Real-time Login Details Cord */}
            {loginStatus?.isLoggedIn && (
              <div className="mt-4 rounded-xl border border-blue-100 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 animate-in slide-in-from-top-2 duration-300">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-blue-600 shadow-inner">
                    <span className="material-symbols-outlined text-[24px]">verified_user</span>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900 leading-tight">Current Session Active</h4>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Logged in {formatDateTime(loginStatus.login_time)}
                      {loginStatus.app_active_seconds ? <> • Active <span className="font-semibold text-slate-700">{fmtSeconds(loginStatus.app_active_seconds)}</span></> : null}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 md:gap-6">
                  <div className="md:text-right">
                    <p className="text-[10px] uppercase font-bold tracking-widest text-blue-600/70 mb-1">Device</p>
                    <p className="text-sm font-bold text-slate-800 break-words" title={loginStatus.device_info || ''}>
                      {loginStatus.device_info || 'Unknown'}
                    </p>
                  </div>
                  <div className="md:text-right">
                    <p className="text-[10px] uppercase font-bold tracking-widest text-blue-600/70 mb-1">Android Version</p>
                    <p className="text-sm font-bold text-slate-800">{loginStatus.os_version || '—'}</p>
                  </div>
                  <div className="md:text-right">
                    <p className="text-[10px] uppercase font-bold tracking-widest text-blue-600/70 mb-1">App Version</p>
                    <p className="text-sm font-bold text-slate-800">{loginStatus.app_version ? `v${loginStatus.app_version}` : '—'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Inspection Analytics Section */}
        {inspectionStats && editRole !== 'admin' && (
          <div className="px-8 pb-6">
            <h3 className="mb-4 text-sm font-bold text-slate-900 uppercase tracking-wider">Inspection Analytics</h3>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="rounded-lg border border-slate-100 bg-indigo-50/30 p-4">
                <p className="text-xs font-medium text-slate-500 mb-1 flex items-center">
                  <span className="material-symbols-outlined text-[16px] mr-1 text-indigo-500">fact_check</span> Total Inspections
                </p>
                <p className="text-2xl font-bold text-slate-900">{inspectionStats.total}</p>
              </div>
              <div className="rounded-lg border border-slate-100 bg-blue-50/30 p-4">
                <p className="text-xs font-medium text-slate-500 mb-1 flex items-center">
                  <span className="material-symbols-outlined text-[16px] mr-1 text-blue-500">local_shipping</span> Container Inspections
                </p>
                <p className="text-2xl font-bold text-blue-700">{inspectionStats.by_type.container}</p>
              </div>
              <div className="rounded-lg border border-slate-100 bg-purple-50/30 p-4">
                <p className="text-xs font-medium text-slate-500 mb-1 flex items-center">
                  <span className="material-symbols-outlined text-[16px] mr-1 text-purple-500">rocket_launch</span> Pilot Inspections
                </p>
                <p className="text-2xl font-bold text-purple-700">{inspectionStats.by_type.pilot}</p>
              </div>
              <div className="rounded-lg border border-slate-100 bg-emerald-50/30 p-4">
                <p className="text-xs font-medium text-slate-500 mb-1 flex items-center">
                  <span className="material-symbols-outlined text-[16px] mr-1 text-emerald-500">texture</span> Fabric Inspections
                </p>
                <p className="text-2xl font-bold text-emerald-700">{inspectionStats.by_type.fabric}</p>
              </div>
              <div className="rounded-lg border border-slate-100 bg-amber-50/30 p-4">
                <p className="text-xs font-medium text-slate-500 mb-1 flex items-center">
                  <span className="material-symbols-outlined text-[16px] mr-1 text-amber-500">precision_manufacturing</span> Line Inspections
                </p>
                <p className="text-2xl font-bold text-amber-700">{inspectionStats.by_type.line}</p>
              </div>
              <div className="rounded-lg border border-slate-100 bg-rose-50/30 p-4">
                <p className="text-xs font-medium text-slate-500 mb-1 flex items-center">
                  <span className="material-symbols-outlined text-[16px] mr-1 text-rose-500">task_alt</span> Final Inspections
                </p>
                <p className="text-2xl font-bold text-rose-700">{inspectionStats.by_type.final}</p>
              </div>
            </div>
            {inspectionStats.last_active && (
              <p className="mt-3 text-xs text-slate-400">
                Last inspection activity: <span className="font-semibold text-slate-600">{formatDateTime(inspectionStats.last_active)}</span>
              </p>
            )}
          </div>
        )}

        <div className="p-8 pt-4">
          <form className="space-y-6">
            <h3 className="mb-4 text-sm font-bold text-slate-900 uppercase tracking-wider">Profile Information</h3>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700" htmlFor="fullname">Full Name</label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <span className="material-symbols-outlined text-[20px] text-slate-400">person</span>
                  </div>
                  <input
                    className="block w-full rounded-lg border-slate-200 py-2.5 pl-10 text-sm text-slate-900 placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce]"
                    id="fullname"
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700" htmlFor="employeeId">Employee ID</label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <span className="material-symbols-outlined text-[20px] text-slate-400">badge</span>
                  </div>
                  <input
                    className="block w-full rounded-lg border-slate-200 py-2.5 pl-10 text-sm text-slate-900 placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce]"
                    id="employeeId"
                    type="text"
                    disabled
                    value={userProfile.employee_id || 'N/A'}
                  />
                </div>
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700" htmlFor="email">Email ID</label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <span className="material-symbols-outlined text-[20px] text-slate-400">mail</span>
                </div>
                <input
                  className="block w-full rounded-lg border-slate-200 py-2.5 pl-10 text-sm text-slate-900 placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce]"
                  id="email"
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700" htmlFor="userType">User Type</label>
                <select
                  className="block w-full rounded-lg border-slate-200 py-2.5 text-sm text-slate-900 focus:border-[#136bce] focus:ring-[#136bce]"
                  id="userType"
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value)}
                >
                  <option value="Manager">QA Manager</option>
                  <option value="Senior Inspector">Senior Inspector</option>
                  <option value="Inspector">QA Inspector</option>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700" htmlFor="status">Status</label>
                <select
                  className="block w-full rounded-lg border-slate-200 py-2.5 text-sm text-slate-900 focus:border-[#136bce] focus:ring-[#136bce]"
                  id="status"
                  value={String(editIsActive)}
                  onChange={(e) => setEditIsActive(e.target.value === 'true')}
                >
                  <option value="true">Active</option>
                  <option value="false">Disabled</option>
                </select>
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700" htmlFor="phone">Phone Number</label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <span className="material-symbols-outlined text-[20px] text-slate-400">call</span>
                </div>
                <input
                  className="block w-full rounded-lg border-slate-200 py-2.5 pl-10 text-sm text-slate-900 placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce]"
                  id="phone"
                  type="text"
                  placeholder="+91 00000 00000"
                  value={editPhone}
                  onChange={(e) => setEditPhone(e.target.value)}
                />
              </div>
            </div>
            {editRole !== 'admin' && (
              <div className="border-t border-slate-100 pt-6">
                <h3 className="mb-4 text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                  <span className="material-symbols-outlined text-[18px] text-blue-600">apps</span>
                  Module Access
                </h3>
                <p className="text-xs text-slate-500 mb-4">Select which inspection modules this user is authorized to access on the mobile application.</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {[...allModules].sort((a, b) => {
                    const order = ['pilot_run', 'fabric_inspection', 'line_inspection', 'final_inspection', 'container_inspection'];
                    const ai = order.indexOf(a.slug);
                    const bi = order.indexOf(b.slug);
                    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
                  }).map((module) => {
                    const isSelected = selectedModuleIds.includes(module.id);
                    return (
                      <div
                        key={module.id}
                        onClick={() => toggleModule(module.id)}
                        className={`relative flex cursor-pointer flex-col rounded-xl border p-4 transition-all duration-200 ${isSelected
                            ? 'border-blue-200 bg-blue-50/50 ring-1 ring-blue-100 shadow-sm'
                            : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                          }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-[20px] material-symbols-outlined ${isSelected ? 'text-blue-600' : 'text-slate-400'}`}>
                            {module.slug === 'pilot_run' ? 'rocket_launch' :
                              module.slug === 'fabric_inspection' ? 'texture' :
                                module.slug === 'line_inspection' ? 'precision_manufacturing' :
                                  module.slug === 'final_inspection' ? 'fact_check' :
                                    module.slug === 'container_inspection' ? 'local_shipping' : 'extension'}
                          </span>
                          <div className={`flex h-5 w-5 items-center justify-center rounded-full border transition-colors ${isSelected ? 'border-blue-600 bg-blue-600' : 'border-slate-300 bg-white'}`}>
                            {isSelected && <span className="material-symbols-outlined text-[14px] text-white font-bold">check</span>}
                          </div>
                        </div>
                        <span className={`text-xs font-bold leading-tight ${isSelected ? 'text-blue-900' : 'text-slate-700'}`}>{module.name}</span>
                        <span className="text-[10px] text-slate-500 mt-1 uppercase tracking-tight">{module.slug.replace('_', ' ')}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="border-t border-slate-100 pt-2"></div>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between rounded-lg bg-slate-50 p-4 border border-slate-100">
              <div>
                <h3 className="text-sm font-medium text-slate-900">Security & Danger Zone</h3>
                <p className="text-xs text-slate-500">Update password or permanently delete this user account.</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setIsPasswordModalOpen(true)}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
                  type="button"
                >
                  <span className="material-symbols-outlined text-[18px]">lock_reset</span>
                  Change Password
                </button>
                <button
                  onClick={() => setIsDeleteModalOpen(true)}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 shadow-sm transition-colors hover:bg-red-50 hover:border-red-300"
                  type="button"
                >
                  <span className="material-symbols-outlined text-[18px]">delete</span>
                  Delete User
                </button>
              </div>
            </div>
          </form>
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-8 py-5">
          <button
            onClick={() => navigate('/user-management')}
            className="rounded-lg border border-slate-300 bg-white px-6 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-900"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveChanges}
            disabled={isSaving}
            className="rounded-lg bg-[#136bce] px-6 py-2.5 text-sm font-medium text-white shadow-sm shadow-blue-500/30 transition-all hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"></div>
                Saving...
              </>
            ) : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Change Password Modal */}
      {isPasswordModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div
            className="fixed inset-0 bg-slate-900 bg-opacity-75 backdrop-blur-sm transition-opacity"
            onClick={() => {
              if (!isUpdatingPassword) {
                setIsPasswordModalOpen(false);
                resetPasswordForm();
              }
            }}
          ></div>
          <span className="hidden sm:inline-block sm:h-screen sm:align-middle">&#8203;</span>
          <div className="relative inline-block transform overflow-hidden rounded-xl bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:align-middle animate-in zoom-in-95 duration-200">
            <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-bold leading-6 text-slate-900">Change Password</h3>
              <button
                onClick={() => {
                  setIsPasswordModalOpen(false);
                  resetPasswordForm();
                }}
                disabled={isUpdatingPassword}
                className="text-slate-400 hover:text-slate-500 focus:outline-none disabled:opacity-50"
                type="button"
              >
                <span className="material-symbols-outlined text-[20px]">close</span>
              </button>
            </div>
            <div className="px-6 py-6">
              <p className="text-sm text-slate-500 mb-6">Create a new password for <span className="font-semibold text-slate-700">{userProfile.name}</span>. You'll need to enter your admin password to confirm this action.</p>

              {passwordError && (
                <div className="mb-4 rounded-lg bg-red-50 p-3 text-xs text-red-700 border border-red-100 flex items-center gap-2">
                  <span className="material-symbols-outlined text-[16px]">error</span>
                  {passwordError}
                </div>
              )}

              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="current-password">Current User Password</label>
                  <input
                    className="block w-full rounded-lg border-slate-200 py-2.5 px-3 text-sm text-slate-900 placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce] disabled:opacity-60"
                    id="current-password"
                    type="password"
                    placeholder="User's current password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    disabled={isUpdatingPassword}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="new-password">New Password</label>
                    <input
                      className="block w-full rounded-lg border-slate-200 py-2.5 px-3 text-sm text-slate-900 placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce] disabled:opacity-60"
                      id="new-password"
                      type="password"
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      disabled={isUpdatingPassword}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="confirm-new-password">Confirm New</label>
                    <input
                      className="block w-full rounded-lg border-slate-200 py-2.5 px-3 text-sm text-slate-900 placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce] disabled:opacity-60"
                      id="confirm-new-password"
                      type="password"
                      placeholder="••••••••"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      required
                      disabled={isUpdatingPassword}
                    />
                  </div>
                </div>
                <div className="border-t border-slate-100 my-4"></div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="admin-password">Admin Password Verification</label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <span className="material-symbols-outlined text-[20px] text-slate-400">admin_panel_settings</span>
                    </div>
                    <input
                      className="block w-full rounded-lg border-slate-200 py-2.5 pl-10 pr-3 text-sm text-slate-900 placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce] disabled:opacity-60"
                      id="admin-password"
                      type="password"
                      placeholder="Enter your admin password"
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                      required
                      disabled={isUpdatingPassword}
                    />
                  </div>
                  <p className="mt-1 text-[10px] text-slate-400 uppercase font-bold tracking-tight">Security check required</p>
                </div>

                <div className="bg-slate-50 -mx-6 -mb-6 px-6 py-4 sm:flex sm:flex-row-reverse sm:gap-3 border-t border-slate-100">
                  <button
                    className="inline-flex w-full justify-center items-center gap-2 rounded-lg bg-[#136bce] px-6 py-2.5 text-sm font-bold text-white shadow-md hover:bg-blue-700 sm:w-auto transition-all disabled:opacity-60"
                    type="submit"
                    disabled={isUpdatingPassword}
                  >
                    {isUpdatingPassword ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"></div>
                        Updating...
                      </>
                    ) : 'Update Password'}
                  </button>
                  <button
                    onClick={() => {
                      setIsPasswordModalOpen(false);
                      resetPasswordForm();
                    }}
                    className="mt-3 inline-flex w-full justify-center rounded-lg border border-slate-300 bg-white px-6 py-2.5 text-sm font-bold text-slate-700 shadow-sm hover:bg-slate-50 sm:mt-0 sm:w-auto transition-colors disabled:opacity-50"
                    type="button"
                    disabled={isUpdatingPassword}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div
            className="fixed inset-0 bg-slate-900 bg-opacity-75 backdrop-blur-sm transition-opacity"
            onClick={() => setIsDeleteModalOpen(false)}
          ></div>
          <span className="hidden sm:inline-block sm:h-screen sm:align-middle">&#8203;</span>
          <div className="relative inline-block transform overflow-hidden rounded-xl bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:align-middle animate-in zoom-in-95 duration-200">
            <div className="p-8 text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-600 ring-4 ring-red-50/50">
                <span className="material-symbols-outlined text-[32px]">warning</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Delete User Account?</h3>
              <p className="text-sm text-slate-500 mb-8">
                Are you sure you want to delete <span className="font-semibold text-slate-700">{userProfile.name}'s</span> account? This action cannot be undone and will remove all system access.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  disabled={isDeleting}
                  className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
                  type="button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  disabled={isDeleting}
                  className="flex-1 rounded-lg bg-red-600 px-4 py-3 text-sm font-bold text-white hover:bg-red-700 shadow-md shadow-red-200 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  type="button"
                >
                  {isDeleting ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"></div>
                      Deleting...
                    </>
                  ) : 'Delete Permanently'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Success Modal */}
      {isSuccessModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-y-auto print:hidden">
          <div
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => setIsSuccessModalOpen(false)}
          ></div>
          <div className="relative w-full max-w-sm transform overflow-hidden rounded-2xl bg-white p-8 shadow-2xl transition-all border border-slate-100 text-center animate-in zoom-in-95 duration-200">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-4 ring-emerald-50/50">
              <span className="material-symbols-outlined text-[32px]">check_circle</span>
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">{successTitle}</h3>
            <p className="text-slate-500 text-sm mb-8">
              {successMessage}
            </p>
            <button
              type="button"
              className="w-full rounded-lg bg-emerald-600 px-4 py-3 text-sm font-bold text-white hover:bg-emerald-700 shadow-md shadow-emerald-200 transition-all active:scale-[0.98]"
              onClick={() => navigate('/user-management')}
            >
              Great, thanks!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetails;
